<div class="tab-component sim-details-tab" [attr.aria-busy]="isLoading">
  <div class="tab-component-wrapper">
    <!-- Top Header: SIM Details & Refresh -->
    <div class="sim-details-header">
      <div class="tab-title">
        <h3>SIM Details</h3>
      </div>
      <div class="sim-details-actions" role="group" aria-label="SIM details actions">
        <span class="last-updated" aria-live="polite">
          <strong>Last Updated (UTC):</strong> {{ lastUpdatedLabel }}
        </span>
        <button type="button" class="button-download refresh-btn" (click)="onRefresh()"
          [disabled]="isLoading || isTealLoading" aria-label="Refresh SIM comparison data">
          <span class="button-icon">
            <mat-icon [ngClass]="{ spin: isLoading }">refresh</mat-icon>
          </span>
          <span class="button-text">Refresh</span>
        </button>
      </div>
    </div>

    <div class="tab-content">
      <!-- Error Message for SIM comparison -->
      <div *ngIf="loadError" class="sim-details-error" role="alert">
        {{ loadError }}
      </div>

      <!-- SIM Comparison Table -->
      <div class="table-responsive">
        <table class="sim-comparison-table" aria-label="MG14 and Teal SIM comparison">
          <thead>
            <tr>
              <th scope="col" class="col-info">SIM Information</th>
              <th scope="col" class="col-source">MG14</th>
              <th scope="col" class="col-source">Teal</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of comparisonRows">
              <th scope="row" class="row-label">
                <mat-icon class="row-icon" aria-hidden="true">{{ row.icon }}</mat-icon>
                <span>{{ row.label }}</span>
              </th>
              <td>{{ row.mg14 }}</td>
              <td>{{ row.teal }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div *ngIf="isLoading" class="sim-details-loading" aria-live="polite">
        Loading SIM comparison...
      </div>

      <!-- TEAL Data Usage Section -->
      <div class="teal-data-usage-section mt-4">
        <div class="teal-section-header">
          <div class="teal-title-group">
            <h4 class="teal-section-title">TEAL Data Usage</h4>

            <span *ngIf="resolvedEid" class="eid-badge" [matTooltip]="'ID: ' + resolvedEid">
              ID: {{ resolvedEid }}
            </span>
          </div>

          <div class="teal-actions-bar" *ngIf="resolvedEid">
            <div class="preset-group">
              <button type="button" class="preset-btn" [class.active]="selectedTealPreset === 'today'"
                (click)="applyTealPreset('today')" [disabled]="isTealLoading">Today</button>
              <button type="button" class="preset-btn" [class.active]="selectedTealPreset === '7days'"
                (click)="applyTealPreset('7days')" [disabled]="isTealLoading">7 Days</button>
              <button type="button" class="preset-btn" [class.active]="selectedTealPreset === '30days'"
                (click)="applyTealPreset('30days')" [disabled]="isTealLoading">30 Days</button>
            </div>

            <div class="date-pickers-group">
              <label for="tealStartDate">From</label>
              <input id="tealStartDate" type="date" class="form-control form-control-sm" [(ngModel)]="tealStartDate"
                (change)="onCustomDateChange()" [disabled]="isTealLoading" />
              <label for="tealEndDate">To</label>
              <input id="tealEndDate" type="date" class="form-control form-control-sm" [(ngModel)]="tealEndDate"
                (change)="onCustomDateChange()" [disabled]="isTealLoading" />
              <button type="button" class="btn btn-sm btn-filter" (click)="onTealFilterSubmit()"
                [disabled]="isTealLoading">Apply</button>
              <button type="button" class="button-download refresh-btn-sm" (click)="onRefreshTealUsage()"
                [disabled]="isTealLoading">
                <mat-icon [ngClass]="{ spin: isTealLoading }">refresh</mat-icon>
                Refresh
              </button>
              <button type="button" class="button-download export-btn-sm" (click)="onExportTealUsage()"
                [disabled]="isTealLoading || isExporting || !tealEntries.length"
                title="Export TEAL usage data to Excel">
                <mat-icon>file_download</mat-icon>
                {{ isExporting ? 'Exporting...' : 'Export' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Waiting for mongo-config response -->
        <div *ngIf="isLoading && !resolvedEid" class="sim-details-loading" aria-live="polite">
          Determining active SIM...
        </div>

        <!-- No SIM EID available after load -->
        <div *ngIf="!resolvedEid && !isLoading" class="no-eid-alert">
          <span>No active SIM EID is recorded for this device. TEAL data usage cannot be retrieved.</span>
        </div>

        <!-- When EID is available -->
        <div *ngIf="resolvedEid" class="teal-section-body">
          <!-- Error Banner -->
          <div *ngIf="tealLoadError" class="sim-details-error mt-2 mb-3">
            <span>{{ tealLoadError }}</span>
            <button type="button" class="btn btn-sm ms-2" (click)="loadTealDataUsage()">Retry</button>
          </div>

          <!-- Summary row -->
          <div class="usage-summary mb-3" *ngIf="tealDataUsage">
            <span><strong>Total:</strong> {{ formatBytes(tealDataUsage.totalUsageBytes) }}
              <span class="text-muted" *ngIf="tealDataUsage.totalUsageBytes">({{ tealDataUsage.totalUsageBytes | number
                }} bytes)</span>
            </span>
            <span class="ms-4"><strong>Records:</strong> {{ tealEntries.length }}</span>
            <span class="ms-4"><strong>Period:</strong> {{ formatPeriodDate(tealDataUsage.startDate) }} –
              {{ formatPeriodDate(tealDataUsage.endDate) }}</span>
          </div>

          <!-- Data Usage Table -->
          <div class="table-responsive" *ngIf="tealEntries.length > 0">
            <table class="teal-usage-table" aria-label="TEAL daily data usage">
              <thead>
                <tr>
                  <th scope="col" class="col-period">Period / Date</th>
                  <th scope="col" class="col-usage">Data Usage</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let entry of tealEntries">
                  <td class="period-cell">
                    <span class="period-badge" [matTooltip]="entry.period || ''">{{ formatPeriodDate(entry.period)
                      }}</span>
                  </td>
                  <td class="usage-cell">
                    <strong class="usage-formatted">{{ formatBytes(entry.usage) }}</strong>
                    <span class="usage-raw" *ngIf="entry.usage">({{ entry.usage | number }} B)</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State -->
          <div *ngIf="!isTealLoading && !tealLoadError && tealEntries.length === 0" class="teal-empty-state">
            No data consumption records found for the selected period
            ({{ formatPeriodDate(tealStartDate) }} to {{ formatPeriodDate(tealEndDate) }}).
          </div>

          <!-- Loading State -->
          <div *ngIf="isTealLoading" class="sim-details-loading py-3">
            Loading TEAL data usage...
          </div>
        </div>
      </div>
    </div>
  </div>
</div>